import React, { useState, useEffect, useRef } from 'react';
import { SpecTableContent, SpecDetailTable } from '../types';
import ConfirmModal from './modals/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

interface SpecTableEditProps {
    spec: SpecDetailTable;
    onChange: (newContent: SpecTableContent) => void;
    setIsModified: (isModified: boolean) => void;
    setHasValidationError?: (hasError: boolean) => void;
}

interface ConfirmActionType {
    type: 'row' | 'column';
    index: number;
}

interface InsertionGap {
    type: 'row' | 'column';
    index: number; // The index where insertion would happen (between index-1 and index)
    isVisible: boolean;
    position?: number; // Actual pixel position for accurate placement
}

const SpecTableEdit: React.FC<SpecTableEditProps> = ({
    spec,
    onChange,
    setIsModified,
    setHasValidationError,
}) => {
    // References
    const tableRef = useRef<HTMLTableElement>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    // State for table data
    const [headers, setHeaders] = useState<string[]>(
        spec.content.headers.map((h) => h ?? ''),
    );
    const [rows, setRows] = useState<string[][]>(
        spec.content.rows.map((row) => row.map((cell) => cell ?? '')),
    );

    // State for hover effects and delete/insert UI
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [hoveredCol, setHoveredCol] = useState<number | null>(null);

    // State for insertion gaps
    const [insertionGap, setInsertionGap] = useState<InsertionGap | null>(null);

    // State for confirmation modal
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] =
        useState<ConfirmActionType | null>(null);

    // --- Basic Handlers ---
    const handleHeaderChange = (colIndex: number, value: string) => {
        const newHeaders = [...headers];
        newHeaders[colIndex] = value;
        setHeaders(newHeaders);
        setIsModified(true);
        onChange({ headers: newHeaders, rows });
    };

    const handleCellChange = (
        rowIndex: number,
        colIndex: number,
        value: string,
    ) => {
        const newRows = [...rows];
        newRows[rowIndex] = [...newRows[rowIndex]];
        newRows[rowIndex][colIndex] = value;
        setRows(newRows);
        setIsModified(true);
        onChange({ headers, rows: newRows });
    };

    // --- Add/Delete Functions ---
    const handleInsertRow = (index: number) => {
        const emptyRow = new Array(headers.length).fill('');
        const newRows = [...rows];
        newRows.splice(index, 0, emptyRow);
        setRows(newRows);
        setIsModified(true);
        onChange({ headers, rows: newRows });
        setInsertionGap(null);
    };

    const handleInsertColumn = (index: number) => {
        const newHeaders = [...headers];
        newHeaders.splice(index, 0, '');
        const newRows = rows.map((row) => {
            const newRow = [...row];
            newRow.splice(index, 0, '');
            return newRow;
        });
        setHeaders(newHeaders);
        setRows(newRows);
        setIsModified(true);
        onChange({ headers: newHeaders, rows: newRows });
        setInsertionGap(null);
    };

    const openDeleteConfirm = (type: 'row' | 'column', index: number) => {
        setConfirmAction({ type, index });
        setIsConfirmOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!confirmAction) return;
        if (confirmAction.type === 'row') {
            if (rows.length > 1) {
                const newRows = [...rows];
                newRows.splice(confirmAction.index, 1);
                setRows(newRows);
                setIsModified(true);
                onChange({ headers, rows: newRows });
            }
        } else {
            if (headers.length > 1) {
                const newHeaders = [...headers];
                newHeaders.splice(confirmAction.index, 1);
                const newRows = rows.map((row) => {
                    const newRow = [...row];
                    newRow.splice(confirmAction.index, 1);
                    return newRow;
                });
                setHeaders(newHeaders);
                setRows(newRows);
                setIsModified(true);
                onChange({ headers: newHeaders, rows: newRows });
            }
        }
        setIsConfirmOpen(false);
        setConfirmAction(null);
    };

    // --- Mouse handling for insertion gaps ---
    const handleTableMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!tableRef.current || !tableWrapperRef.current) return;

        const wrapperRect = tableWrapperRef.current.getBoundingClientRect();
        const mouseX = e.clientX - wrapperRect.left;
        const mouseY = e.clientY - wrapperRect.top;
        const HOVER_THRESHOLD = 5;
        const ROW_DELETE_ZONE_X_LIMIT = 0;

        const tableRows = tableRef.current.querySelectorAll('tbody tr');
        const headerCells = tableRef.current.querySelectorAll('thead th');
        const headerRow = tableRef.current.querySelector('thead tr');

        setInsertionGap(null);
        let foundGap = false;

        if (headerRow && tableRows.length > 0) {
            const firstRowRect = tableRows[0].getBoundingClientRect();
            const firstRowTopEdge = firstRowRect.top - wrapperRect.top;
            if (Math.abs(mouseY - firstRowTopEdge) < HOVER_THRESHOLD) {
                setInsertionGap({
                    type: 'row',
                    index: 0,
                    isVisible: true,
                    position: firstRowTopEdge,
                });
                foundGap = true;
            }
        }

        if (!foundGap) {
            tableRows.forEach((row, index) => {
                if (foundGap) return;
                const rowRect = row.getBoundingClientRect();
                const rowBottomEdge = rowRect.bottom - wrapperRect.top;
                if (Math.abs(mouseY - rowBottomEdge) < HOVER_THRESHOLD) {
                    setInsertionGap({
                        type: 'row',
                        index: index + 1,
                        isVisible: true,
                        position: rowBottomEdge,
                    });
                    foundGap = true;
                }
            });
        }

        if (
            !foundGap &&
            mouseX > ROW_DELETE_ZONE_X_LIMIT &&
            headerCells.length > 0
        ) {
            const firstCell = headerCells[0];
            const firstCellRect = firstCell.getBoundingClientRect();
            const firstCellLeftEdge = firstCellRect.left - wrapperRect.left;
            if (Math.abs(mouseX - firstCellLeftEdge) < HOVER_THRESHOLD) {
                setInsertionGap({
                    type: 'column',
                    index: 0,
                    isVisible: true,
                    position: firstCellLeftEdge,
                });
                foundGap = true;
            }
        }

        if (!foundGap) {
            headerCells.forEach((cell, index) => {
                if (foundGap) return;
                if (index === 0) {
                    const firstCellLeftEdge =
                        cell.getBoundingClientRect().left - wrapperRect.left;
                    if (Math.abs(mouseX - firstCellLeftEdge) < HOVER_THRESHOLD)
                        return;
                }
                const cellRect = cell.getBoundingClientRect();
                const cellRightEdge = cellRect.right - wrapperRect.left;
                if (Math.abs(mouseX - cellRightEdge) < HOVER_THRESHOLD) {
                    setInsertionGap({
                        type: 'column',
                        index: index + 1,
                        isVisible: true,
                        position: cellRightEdge,
                    });
                    foundGap = true;
                }
            });
        }
    };

    const handleTableMouseLeave = () => {
        setInsertionGap(null);
    };

    // --- Validation ---
    const validateTable = () => {
        if (headers.length === 0 || rows.length === 0) return true;
        for (let colIndex = 0; colIndex < headers.length; colIndex++) {
            if (
                headers[colIndex].trim() === '' &&
                rows.every((row) => row[colIndex].trim() === '')
            ) {
                return false;
            }
        }
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            if (rows[rowIndex].every((cell) => cell.trim() === '')) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        const isValid = validateTable();
        if (setHasValidationError) {
            setHasValidationError(!isValid);
        }
    }, [headers, rows, setHasValidationError]);

    // Make cells fully clickable
    const focusInputInCell = (e: React.MouseEvent<HTMLTableCellElement>) => {
        const cell = e.currentTarget;
        const input = cell.querySelector('input');
        if (input && e.target !== input) {
            input.focus();
        }
    };

    return (
        <div className="spec-edit-table-container">
            <div
                ref={tableWrapperRef}
                className="table-wrapper"
                onMouseMove={handleTableMouseMove}
                onMouseLeave={handleTableMouseLeave}
            >
                <table ref={tableRef} className="nested-table editable-table">
                    <thead>
                        <tr>
                            {headers.map((header, colIndex) => (
                                <th
                                    key={colIndex}
                                    className={`header-cell ${hoveredCol === colIndex ? 'hover' : ''}`}
                                    onMouseEnter={() => setHoveredCol(colIndex)}
                                    onMouseLeave={() => setHoveredCol(null)}
                                    onClick={focusInputInCell}
                                >
                                    <input
                                        type="text"
                                        className="table-input header-input"
                                        value={header}
                                        onChange={(e) =>
                                            handleHeaderChange(
                                                colIndex,
                                                e.target.value,
                                            )
                                        }
                                        placeholder={`Header ${colIndex + 1}`}
                                    />
                                    {hoveredCol === colIndex && (
                                        <button
                                            className="table-action-button delete-button column-delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteConfirm(
                                                    'column',
                                                    colIndex,
                                                );
                                            }}
                                            title="Delete column"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className={`data-row ${hoveredRow === rowIndex ? 'hover' : ''}`}
                                onMouseEnter={() => setHoveredRow(rowIndex)}
                                onMouseLeave={() => setHoveredRow(null)}
                            >
                                {row.map((cell, colIndex) => (
                                    <td
                                        key={colIndex}
                                        className="data-cell"
                                        onClick={focusInputInCell}
                                        onMouseEnter={() =>
                                            setHoveredCol(colIndex)
                                        }
                                        onMouseLeave={() => setHoveredCol(null)}
                                    >
                                        <input
                                            type="text"
                                            className="table-input"
                                            value={cell}
                                            onChange={(e) =>
                                                handleCellChange(
                                                    rowIndex,
                                                    colIndex,
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="Enter text..."
                                        />
                                        {colIndex === 0 &&
                                            hoveredRow === rowIndex && (
                                                <button
                                                    className="table-action-button delete-button row-delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteConfirm(
                                                            'row',
                                                            rowIndex,
                                                        );
                                                    }}
                                                    title="Delete row"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                    />
                                                </button>
                                            )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {insertionGap && insertionGap.isVisible && (
                    <>
                        {insertionGap.type === 'row' && (
                            <div
                                className="insertion-gap row-insertion-gap"
                                style={{
                                    top: insertionGap.position
                                        ? `${insertionGap.position}px`
                                        : 'auto',
                                }}
                                onClick={() =>
                                    handleInsertRow(insertionGap.index)
                                }
                            >
                                <button
                                    className="insertion-button"
                                    title="Insert row"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                        )}
                        {insertionGap.type === 'column' && (
                            <div
                                className="insertion-gap column-insertion-gap"
                                style={{
                                    left: insertionGap.position
                                        ? `${insertionGap.position}px`
                                        : 'auto',
                                }}
                                onClick={() =>
                                    handleInsertColumn(insertionGap.index)
                                }
                            >
                                <button
                                    className="insertion-button"
                                    title="Insert column"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            {(headers.length === 0 || rows.length === 0) && (
                <div className="empty-table-actions">
                    {headers.length === 0 && (
                        <button onClick={() => handleInsertColumn(0)}>
                            Add Column
                        </button>
                    )}
                    {rows.length === 0 && (
                        <button onClick={() => handleInsertRow(0)}>
                            Add Row
                        </button>
                    )}
                </div>
            )}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                heading={`Delete ${confirmAction?.type === 'row' ? 'Row' : 'Column'}`}
                text={`Are you sure you want to delete this ${confirmAction?.type === 'row' ? 'row' : 'column'}? This action cannot be undone.`}
                confirmButtonText="Delete"
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export default SpecTableEdit;
